import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ACTIVITY_ROLES, ACTIVITY_TYPE_KEY_RE, type ActivityRole, type ActivityType } from "@torre/work-hours";
import Modal from "../../../components/base/Modal";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses } from "../theme";
import { useCatalog } from "../hooks/useActivityData";
import { activityErrorCode, activityErrorMessage, deleteActivityType, saveActivityType } from "../services/activitiesService";

// ---------------------------------------------------------------------------
// Catálogo de actividades por puesto (#1305). Es la MISMA lista que el «Tipo»
// de Horas › Registrar tiempo: cada tipo dice qué puestos pueden realizarlo.
// Los dueños editan; el resto ve el catálogo y qué le toca.
// ---------------------------------------------------------------------------

interface FormState {
  key: string;
  name: string;
  nameEn: string;
  description: string;
  icon: string;
  everyone: boolean;
  roles: ActivityRole[];
  manualEntry: boolean;
  active: boolean;
}

const emptyForm = (): FormState => ({ key: "", name: "", nameEn: "", description: "", icon: "ri-clipboard-line", everyone: true, roles: [], manualEntry: true, active: true });
const formOf = (ty: ActivityType): FormState => ({
  key: ty.key,
  name: ty.name,
  nameEn: ty.nameEn ?? "",
  description: ty.description ?? "",
  icon: ty.icon,
  everyone: ty.roles === "all",
  roles: ty.roles === "all" ? [] : [...ty.roles],
  manualEntry: ty.manualEntry,
  active: ty.active,
});

export default function CatalogoSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const { catalog, meta, loading, error, reload } = useCatalog();
  const canManage = Boolean(meta?.canManage);
  const allowed = useMemo(() => new Set(meta?.allowedKeys ?? []), [meta]);

  const [editing, setEditing] = useState<{ mode: "create" | "edit"; form: FormState } | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const roleLabel = (r: string) => t(`team.roles.${r}`, { defaultValue: r });

  const submit = async () => {
    if (!editing) return;
    const f = editing.form;
    setFormError(null);
    if (!ACTIVITY_TYPE_KEY_RE.test(f.key.trim().toLowerCase())) return setFormError(t("staffActivities.catalog.form.errors.key"));
    if (f.name.trim().length < 2 || f.name.trim().length > 60) return setFormError(t("staffActivities.catalog.form.errors.name"));
    if (!f.everyone && f.roles.length === 0) return setFormError(t("staffActivities.catalog.form.errors.roles"));
    setSaving(true);
    try {
      await saveActivityType({
        key: f.key.trim().toLowerCase(),
        name: f.name.trim(),
        nameEn: f.nameEn.trim() || null,
        description: f.description.trim() || null,
        icon: f.icon.trim() || "ri-clipboard-line",
        roles: f.everyone ? "all" : f.roles,
        manualEntry: f.manualEntry,
        active: f.active,
      });
      setEditing(null);
      reload();
    } catch (err) {
      setFormError(activityErrorMessage(err) ?? t("staffActivities.catalog.form.errors.save"));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (ty: ActivityType) => {
    setNotice(null);
    try {
      await saveActivityType({ key: ty.key, name: ty.name, nameEn: ty.nameEn, description: ty.description, icon: ty.icon, roles: ty.roles, manualEntry: ty.manualEntry, order: ty.order, active: !ty.active });
      reload();
    } catch (err) {
      setNotice(activityErrorMessage(err) ?? t("staffActivities.common.error"));
    }
  };

  const remove = async (ty: ActivityType) => {
    setNotice(null);
    if (ty.builtIn) return setNotice(t("staffActivities.catalog.builtInNoDelete"));
    if (!window.confirm(t("staffActivities.catalog.deleteConfirm"))) return;
    try {
      await deleteActivityType(ty.key);
      reload();
    } catch (err) {
      setNotice(activityErrorCode(err) === "409" ? t("staffActivities.catalog.builtInNoDelete") : activityErrorMessage(err) ?? t("staffActivities.common.error"));
    }
  };

  const fieldBase = `w-full rounded-lg border px-3 py-2 text-sm outline-none ${tc.border} ${tc.cardBg} ${tc.text}`;
  const labelBase = `mb-1 block text-xs font-medium ${tc.textSecondary}`;
  const visible = canManage ? catalog : catalog.filter((ty) => ty.active);

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-clipboard-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>{t("staffActivities.catalog.title")}</h1>
              <p className={`text-sm ${tc.textSecondary}`}>{t("staffActivities.catalog.subtitle")}</p>
            </div>
          </div>
          {canManage ? (
            <button type="button" data-testid="catalog-new-type" onClick={() => { setFormError(null); setEditing({ mode: "create", form: emptyForm() }); }} className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-400">
              <i className="ri-add-line" /> {t("staffActivities.catalog.newType")}
            </button>
          ) : (
            meta && (
              <p className={`text-xs ${tc.textSecondary}`} data-testid="catalog-readonly">
                {meta.role ? t("staffActivities.catalog.yourRole", { role: roleLabel(meta.role) }) : t("staffActivities.catalog.noRole")} · {t("staffActivities.catalog.readOnly")}
              </p>
            )
          )}
        </div>

        {notice && (
          <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-600" data-testid="catalog-notice">
            <i className="ri-information-line mr-1" />
            {notice}
          </div>
        )}
        {loading && <p className={`text-sm ${tc.textSecondary}`}>{t("staffActivities.common.loading")}</p>}
        {error && (
          <p className="text-sm text-red-500">
            {t("staffActivities.common.error")}{" "}
            <button type="button" onClick={reload} className="underline">
              {t("staffActivities.common.retry")}
            </button>
          </p>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((ty) => {
            const mine = allowed.has(ty.key);
            return (
              <div key={ty.key} data-testid={`catalog-card-${ty.key}`} className={`flex flex-col gap-2 rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg} ${ty.active ? "" : "opacity-60"} ${mine && !canManage ? "ring-1 ring-violet-500/50" : ""}`}>
                <div className="flex items-start justify-between gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500/15 text-primary-500">
                    <i className={`${ty.icon} text-lg`} />
                  </span>
                  <div className="flex items-center gap-1">
                    {mine && !canManage && <span className="rounded-full bg-violet-500/15 px-2 py-0.5 text-[11px] font-medium text-violet-600" data-testid="catalog-mine">{t("staffActivities.catalog.mine")}</span>}
                    {canManage && (
                      <>
                        <button type="button" title={t("staffActivities.catalog.edit")} data-testid={`catalog-edit-${ty.key}`} onClick={() => { setFormError(null); setEditing({ mode: "edit", form: formOf(ty) }); }} className={`rounded-md p-1.5 ${tc.buttonText} ${tc.buttonHover}`}>
                          <i className="ri-edit-line" />
                        </button>
                        <button type="button" title={ty.active ? t("staffActivities.catalog.deactivate") : t("staffActivities.catalog.activate")} onClick={() => void toggleActive(ty)} className={`rounded-md p-1.5 ${tc.buttonText} ${tc.buttonHover}`}>
                          <i className={ty.active ? "ri-toggle-fill text-emerald-500" : "ri-toggle-line"} />
                        </button>
                        {!ty.builtIn && (
                          <button type="button" title={t("staffActivities.catalog.delete")} onClick={() => void remove(ty)} className="rounded-md p-1.5 text-red-500 hover:bg-red-500/10">
                            <i className="ri-delete-bin-line" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <p className={`font-semibold ${tc.text}`}>
                    {ty.name} <span className={`font-mono text-[11px] font-normal ${tc.textSecondary}`}>{ty.key}</span>
                  </p>
                  {ty.description && <p className={`text-xs ${tc.textSecondary}`}>{ty.description}</p>}
                </div>
                <div className="flex flex-wrap gap-1.5 text-[11px]">
                  {ty.roles === "all" ? (
                    <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-emerald-600" data-testid="catalog-role-chip">{t("staffActivities.catalog.allRoles")}</span>
                  ) : (
                    ty.roles.map((r) => (
                      <span key={r} className="rounded-full bg-primary-500/10 px-2 py-0.5 text-primary-500" data-testid="catalog-role-chip">
                        {roleLabel(r)}
                      </span>
                    ))
                  )}
                </div>
                <div className={`mt-auto flex flex-wrap gap-2 text-[11px] ${tc.textSecondary}`}>
                  <span className={`rounded-full px-2 py-0.5 ${ty.manualEntry ? "bg-sky-500/15 text-sky-600" : "bg-gray-400/15"}`}>
                    <i className={`${ty.manualEntry ? "ri-edit-2-line" : "ri-git-commit-line"} mr-1`} />
                    {ty.manualEntry ? t("staffActivities.catalog.manual") : t("staffActivities.catalog.commitsOnly")}
                  </span>
                  <span className="rounded-full bg-gray-400/15 px-2 py-0.5">{ty.builtIn ? t("staffActivities.catalog.builtIn") : t("staffActivities.catalog.custom")}</span>
                  {!ty.active && <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-red-500">{t("staffActivities.catalog.inactive")}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {editing && (
        <Modal
          title={t(editing.mode === "edit" ? "staffActivities.catalog.form.editTitle" : "staffActivities.catalog.form.createTitle")}
          themeClasses={tc}
          onClose={() => !saving && setEditing(null)}
          width="max-w-xl"
          content={
            <div className="space-y-4" data-testid="catalog-form">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelBase}>{t("staffActivities.catalog.form.key")} *</label>
                  <input value={editing.form.key} disabled={editing.mode === "edit"} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, key: e.target.value } })} placeholder="ej. podcast" className={`${fieldBase} font-mono disabled:opacity-60`} aria-label={t("staffActivities.catalog.form.key")} />
                  <p className={`mt-1 text-[11px] ${tc.textSecondary}`}>{t("staffActivities.catalog.form.keyHint")}</p>
                </div>
                <div>
                  <label className={labelBase}>{t("staffActivities.catalog.form.icon")}</label>
                  <div className="flex items-center gap-2">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-500/15 text-primary-500">
                      <i className={`${editing.form.icon || "ri-clipboard-line"} text-lg`} />
                    </span>
                    <input value={editing.form.icon} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, icon: e.target.value } })} placeholder="ri-clipboard-line" className={`${fieldBase} font-mono`} aria-label={t("staffActivities.catalog.form.icon")} />
                  </div>
                </div>
                <div>
                  <label className={labelBase}>{t("staffActivities.catalog.form.name")} *</label>
                  <input value={editing.form.name} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, name: e.target.value } })} className={fieldBase} aria-label={t("staffActivities.catalog.form.name")} />
                </div>
                <div>
                  <label className={labelBase}>{t("staffActivities.catalog.form.nameEn")}</label>
                  <input value={editing.form.nameEn} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, nameEn: e.target.value } })} className={fieldBase} aria-label={t("staffActivities.catalog.form.nameEn")} />
                </div>
              </div>
              <div>
                <label className={labelBase}>{t("staffActivities.catalog.form.description")}</label>
                <textarea rows={2} value={editing.form.description} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, description: e.target.value } })} className={`${fieldBase} resize-none`} aria-label={t("staffActivities.catalog.form.description")} />
              </div>
              <div>
                <label className={labelBase}>{t("staffActivities.catalog.form.roles")}</label>
                <label className={`mb-2 flex items-center gap-2 text-sm ${tc.text}`}>
                  <input type="checkbox" checked={editing.form.everyone} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, everyone: e.target.checked } })} className="h-4 w-4 accent-primary-500" />
                  {t("staffActivities.catalog.form.everyone")}
                </label>
                {!editing.form.everyone && (
                  <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                    {ACTIVITY_ROLES.map((r) => {
                      const on = editing.form.roles.includes(r);
                      return (
                        <label key={r} className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 text-xs ${tc.border} ${tc.text}`}>
                          <input type="checkbox" checked={on} onChange={() => setEditing({ ...editing, form: { ...editing.form, roles: on ? editing.form.roles.filter((x) => x !== r) : [...editing.form.roles, r] } })} className="h-4 w-4 accent-primary-500" />
                          {roleLabel(r)}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-6">
                <label className={`flex items-center gap-2 text-sm ${tc.text}`}>
                  <input type="checkbox" checked={editing.form.manualEntry} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, manualEntry: e.target.checked } })} className="h-4 w-4 accent-primary-500" />
                  {t("staffActivities.catalog.form.manualEntry")}
                </label>
                <label className={`flex items-center gap-2 text-sm ${tc.text}`}>
                  <input type="checkbox" checked={editing.form.active} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, active: e.target.checked } })} className="h-4 w-4 accent-primary-500" />
                  {t("staffActivities.catalog.form.active")}
                </label>
              </div>
              {formError && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-500" data-testid="catalog-form-error">
                  <i className="ri-error-warning-line mr-1" />
                  {formError}
                </div>
              )}
              <div className="flex justify-end gap-2 border-t pt-4 dark:border-dark-border">
                <button type="button" onClick={() => setEditing(null)} disabled={saving} className={`rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.text} ${tc.buttonHover}`}>
                  {t("staffActivities.common.cancel")}
                </button>
                <button type="button" onClick={() => void submit()} disabled={saving} className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-400 disabled:opacity-60">
                  {t("staffActivities.catalog.form.save")}
                </button>
              </div>
            </div>
          }
        />
      )}
    </div>
  );
}
